import type { QuickNote, TaskTemplate } from "../../domain/entities.ts";
import type { Root, SavedView, ViewFilters } from "../../domain/app-types.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import type { PortableArchive, PortableQuickNote, PortableSpace } from "./transfer.ts";
import { parseContentHistory } from "../notes/content-history.ts";
import { isCrossSpace } from "../navigation/view-scope.ts";
import type { exportPortableSpace } from "./export-controller.ts";
import type { importPortableSpace } from "./import-controller.ts";
import type { restorePortableArchive } from "./restore-controller.ts";
import { dailyNoteId, validDailyDate } from "../quick-notes/daily-notes.ts";
import type { ProgressReporter } from "../../ui/operation-progress.ts";

export interface PortableDataOperations {
  exportSpace: typeof exportPortableSpace;
  importSpace: typeof importPortableSpace;
  restoreArchive: typeof restorePortableArchive;
  changeQuickNote(id: string, rootId: string, actorId: string, update: Partial<QuickNote>): Promise<unknown>;
}

export interface PortableDataCapabilities {
  actorId: string;
  roots(): Root[];
  addRoot(root: Root): void;
  activeRoot(rootId: string): Promise<void>;
  personalRootId(): string | null;
  entities(rootId?: string): Promise<StoredEntity[]>;
  request(path: string, init?: RequestInit): Promise<Response>;
  optionalRequest(path: string, init?: RequestInit): Promise<Response | null>;
  apiError(response: Response, fallback: string): Promise<string>;
  synchronize(): Promise<void>;
  savedViews(): SavedView[];
  replaceSavedViews(views: SavedView[]): void;
  persistSavedViews(): Promise<void>;
  personalTemplates(): TaskTemplate[];
  replacePersonalTemplates(templates: TaskTemplate[]): void;
  persistPersonalTemplates(templates: TaskTemplate[]): Promise<void>;
  defaultFilters(): ViewFilters;
  randomId(): string;
  markLocal(): void;
  now(): number;
  operations: PortableDataOperations;
}

export function createPortableDataController(capabilities: PortableDataCapabilities) {
  async function portableSpace(rootId: string, progress?: ProgressReporter): Promise<PortableSpace> {
    const root = capabilities.roots().find(item => item.id === rootId);
    if (!root) throw new Error("Space not found");
    progress?.({ stage: "Reading space data" });
    return capabilities.operations.exportSpace(root, await capabilities.entities(rootId), capabilities.optionalRequest, progress);
  }

  async function importSpace(space: PortableSpace, targetRootId: string, views: SavedView[] = [], progress?: ProgressReporter, mapped?: (collections: ReadonlyMap<string, string>) => void): Promise<void> {
    await capabilities.operations.importSpace(space, targetRootId, views, {
      actorId: capabilities.actorId,
      request: capabilities.request,
      synchronize: capabilities.synchronize,
      defaultFilters: capabilities.defaultFilters,
      addSavedViews: async imported => {
        capabilities.replaceSavedViews([...capabilities.savedViews(), ...imported]);
        await capabilities.persistSavedViews();
      },
      markLocal: capabilities.markLocal,
      progress,
      mapped,
    });
  }

  async function importPersonalTemplates(templates: TaskTemplate[]): Promise<void> {
    if (!templates.length) return;
    const imported = templates.filter(template => !template.deleted).map(template => ({ ...structuredClone(template), id: capabilities.randomId(), rootId: "", createdAt: capabilities.now(), updatedAt: capabilities.now() }));
    const next = [...capabilities.personalTemplates(), ...imported];
    capabilities.replacePersonalTemplates(next);
    await capabilities.persistPersonalTemplates(next);
  }

  async function portableQuickNotes(): Promise<PortableQuickNote[]> {
    const rootId = capabilities.personalRootId();
    if (!rootId) return [];
    const notes: PortableQuickNote[] = (await capabilities.entities(rootId)).filter(entity => entity.kind === "quick_note").map(entity => structuredClone(entity.projection as QuickNote)).filter(note => !note.deleted);
    for (const note of notes) {
      const response = await capabilities.request(`/api/v1/entities/${note.id}/content-history`);
      if (!response.ok) throw new Error("Connect to include private note versions in this backup.");
      const history = parseContentHistory(await response.json(), "quick_note");
      if (history.length) note.contentHistory = history;
    }
    return notes;
  }

  async function importQuickNotes(notes: PortableQuickNote[]): Promise<void> {
    const rootId = capabilities.personalRootId();
    if (!rootId || !notes.length) return;
    const restoreHistory = async (source: PortableQuickNote, id: string) => {
      if (!source.contentHistory?.length) return;
      await capabilities.synchronize();
      const history = parseContentHistory(source.contentHistory, "quick_note");
      const response = await capabilities.request(`/api/v1/entities/${id}/content-history`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(history) });
      if (!response.ok) throw new Error("Note text was imported, but its versions could not be restored. Keep the source backup.");
    };
    for (const source of notes.filter(note => !note.deleted && note.body.trim())) {
      if (validDailyDate(source.dailyDate)) {
        const id = await dailyNoteId(rootId, source.dailyDate);
        const existing = (await capabilities.entities(rootId)).find(entity => entity.id === id);
        if (!existing) {
          await capabilities.operations.changeQuickNote(id, rootId, capabilities.actorId, { dailyDate: source.dailyDate, body: source.body.slice(0, 100_000), pinned: false, archivedAt: null, deleted: false });
          await restoreHistory(source, id);
          continue;
        }
        if ((existing.projection as QuickNote).body === source.body && !(existing.projection as QuickNote).deleted) { await restoreHistory(source, id); continue; }
        // Non-destructive restore: keep a differing/tombstoned day's imported text as an ordinary note.
        const copyId = capabilities.randomId();
        await capabilities.operations.changeQuickNote(copyId, rootId, capabilities.actorId, { body: `Daily note — ${source.dailyDate}\n\n${source.body}`.slice(0, 100_000), pinned: false, archivedAt: null, convertedTaskId: null, deleted: false });
        await restoreHistory(source, copyId);
        continue;
      }
      const id = capabilities.randomId();
      await capabilities.operations.changeQuickNote(id, rootId, capabilities.actorId, { body: source.body.slice(0, 100_000), pinned: !!source.pinned, archivedAt: source.archivedAt || null, convertedTaskId: null, deleted: false });
      await restoreHistory(source, id);
    }
    capabilities.markLocal();
  }

  async function restoreArchive(archive: PortableArchive, progress?: ProgressReporter): Promise<void> {
    const roots = new Map<string, string>(), collections = new Map<string, string>();
    await capabilities.operations.restoreArchive(archive, {
      request: capabilities.request,
      apiError: capabilities.apiError,
      addRoot: capabilities.addRoot,
      importSpace: async (space, root, views, progress) => {
        roots.set(space.id, root);
        await importSpace(space, root, views.filter(view => !isCrossSpace(view.filters)), progress, mapping => mapping.forEach((value, key) => collections.set(key, value)));
      },
      importTemplates: importPersonalTemplates,
      importNotes: importQuickNotes,
      setActiveRoot: capabilities.activeRoot,
      progress,
    });
    const aggregateViews = (archive.savedViews as SavedView[]).filter(view => isCrossSpace(view.filters)).map(view => ({ ...structuredClone(view), id: capabilities.randomId(), published: false, rootId: roots.get(view.rootId || "") || view.rootId, filters: { ...structuredClone(view.filters), spaceIds: view.filters.spaceIds?.map(id => roots.get(id) || id), collectionIds: view.filters.collectionIds?.map(id => collections.get(id) || id), categoryId: collections.get(view.filters.categoryId) || view.filters.categoryId } }));
    if (aggregateViews.length) { capabilities.replaceSavedViews([...capabilities.savedViews(), ...aggregateViews]); await capabilities.persistSavedViews(); }
  }

  return { portableSpace, importSpace, restoreArchive, portableQuickNotes, importQuickNotes, importPersonalTemplates };
}
